import { useMemo, useState } from 'react';
import { CpmButton, CpmModal, CpmTypeChip } from '../../../../components/design-system';
import {
    CPM_BAND_OPTIONS,
    CPM_LICENCE_EXCISE_COPY,
    CPM_METHOD_OPTIONS,
    CPM_RECORD_TYPE_OPTIONS,
    CPM_SIGNAL_OPTIONS,
    formatCpmCount,
    formatCreatedVia,
    formatFlaggedDate,
    packLabel,
} from '../../../../constants/centralProductMaster.constants';
import type {
    CpmBulkResult,
    CpmLinkReviewItem,
    CpmPendingItem,
    CpmQueueTab,
} from '../../../../types/centralProductMaster.types';

type BulkPath = 'picked' | 'filtered';
type BulkKind = 'approve' | 'resolve';
type Phase = 'confirm' | 'working' | 'error' | 'done';

export interface BulkActionModalProps {
    open: boolean;
    kind: BulkKind;
    path: BulkPath;
    tab: CpmQueueTab;
    total: number;
    /** Selected or matching rows for impact / sample / list. */
    rows: Array<CpmPendingItem | CpmLinkReviewItem>;
    filters: {
        band: string[];
        signals: string[];
        method: string[];
        recordType: string[];
        storeBrandLabel: string | null;
        dateFrom: string | null;
        dateTo: string | null;
    };
    remainingAfter?: number;
    onClose: () => void;
    onConfirm: () => Promise<CpmBulkResult | null>;
    onJumpBlocked?: () => void;
}

function isPending(row: CpmPendingItem | CpmLinkReviewItem): row is CpmPendingItem {
    return 'recordType' in row && 'level' in row;
}

function rowType(row: CpmPendingItem | CpmLinkReviewItem): string {
    if (isPending(row)) return row.recordType;
    return row.sourceType;
}

function rowName(row: CpmPendingItem | CpmLinkReviewItem): string {
    if (isPending(row)) return row.name;
    return row.name?.trim() || row.sourceId || '—';
}

function pendingBrandLabel(row: CpmPendingItem): string | null {
    if (row.level === 'brand') return null;
    return row.brand?.name || (row.parent?.level === 'brand' ? row.parent.name : null) || null;
}

function failGroupLabel(reason: string): string {
    const r = reason.toLowerCase();
    if (r.includes('brand')) return 'Blocked — parent brand not approved';
    if (r.includes('product')) return 'Blocked — parent product not approved';
    if (r.includes('parent') || r.includes('block')) return 'Blocked — parent not approved';
    return reason || 'Could not approve';
}

function failGroupHint(reason: string): string {
    const r = reason.toLowerCase();
    if (r.includes('parent') || r.includes('block')) {
        return 'Approve each blocked item’s parent first (or include the parent in the same batch), then re-run these.';
    }
    return 'These rows were left unchanged in the queue.';
}

export default function BulkActionModal({
    open,
    kind,
    path,
    tab,
    total,
    rows,
    filters,
    remainingAfter,
    onClose,
    onConfirm,
    onJumpBlocked,
}: BulkActionModalProps) {
    const [phase, setPhase] = useState<Phase>('confirm');
    const [acked, setAcked] = useState(false);
    const [listOpen, setListOpen] = useState(false);
    const [sampleIdx, setSampleIdx] = useState(0);
    const [result, setResult] = useState<CpmBulkResult | null>(null);

    const filtered = path === 'filtered';
    const isLink = kind === 'resolve' || tab === 'link';

    const counts = useMemo(() => {
        let brands = 0;
        let products = 0;
        let variants = 0;
        rows.forEach((r) => {
            const t = String(rowType(r)).toUpperCase();
            if (t.includes('BRAND')) brands += 1;
            else if (t.includes('PRODUCT')) products += 1;
            else variants += 1;
        });
        return [brands, products, variants] as const;
    }, [rows]);

    const storeBrandCount = useMemo(() => {
        const ids = new Set<string>();
        rows.forEach((r) => {
            const id = isPending(r) ? r.provenance?.firstSeenTenant : r.storeBrand;
            if (id) ids.add(id);
        });
        return ids.size;
    }, [rows]);

    const sample = useMemo(() => {
        if (!rows.length) return [] as typeof rows;
        const start = (sampleIdx * 6) % rows.length;
        const out: typeof rows = [];
        for (let i = 0; i < Math.min(6, rows.length); i += 1) {
            out.push(rows[(start + i) % rows.length]);
        }
        return out;
    }, [rows, sampleIdx]);

    const criteria = useMemo(() => {
        const bandLabel =
            filters.band.length === 0
                ? 'Any band'
                : filters.band
                      .map((b) => CPM_BAND_OPTIONS.find((o) => o.value === b)?.label ?? b)
                      .join(', ');
        const methodLabel =
            filters.method.length === 0
                ? 'Any method'
                : filters.method
                      .map((m) => CPM_METHOD_OPTIONS.find((o) => o.value === m)?.label ?? m)
                      .join(', ');
        const typeLabel =
            filters.recordType.length === 0
                ? 'Any'
                : filters.recordType
                      .map((t) => CPM_RECORD_TYPE_OPTIONS.find((o) => o.value === t)?.label ?? t)
                      .join(', ');
        const signalLabel =
            filters.signals.length === 0
                ? 'Any'
                : filters.signals
                      .map((s) => CPM_SIGNAL_OPTIONS.find((o) => o.value === s)?.label ?? s)
                      .join(', ');
        const dateLabel =
            filters.dateFrom || filters.dateTo
                ? [formatFlaggedDate(filters.dateFrom), formatFlaggedDate(filters.dateTo)]
                      .filter((x) => x !== '—')
                      .join(' – ')
                : 'Any';

        if (isLink) {
            return [
                { k: 'Status', v: 'Awaiting link decision' },
                { k: 'Decision', v: 'Link to suggested master (LINK)' },
                { k: 'Confidence', v: bandLabel },
                { k: 'Method', v: methodLabel },
                { k: 'Record type', v: typeLabel },
                { k: 'Store brand', v: filters.storeBrandLabel || 'All' },
                { k: 'Flagged', v: dateLabel },
            ];
        }
        return [
            { k: 'Status', v: 'Pending' },
            { k: 'Record type', v: typeLabel },
            { k: 'Decision', v: 'Created New (CREATE_NEW)' },
            { k: 'Signal', v: signalLabel },
            { k: 'Confidence', v: bandLabel },
            { k: 'Method', v: methodLabel },
            { k: 'Store brand', v: filters.storeBrandLabel || 'All' },
            { k: 'Created', v: dateLabel },
        ];
    }, [filters, isLink]);

    const canApprove = !filtered || acked;
    const succeeded = result?.succeeded.length ?? 0;
    const failed = result?.failed.length ?? 0;
    const failGroups = useMemo(() => {
        const map = new Map<string, number>();
        (result?.failed ?? []).forEach((f) => {
            const key = f.reason || 'unknown';
            map.set(key, (map.get(key) ?? 0) + 1);
        });
        return Array.from(map.entries())
            .map(([reason, count]) => ({ reason, count }))
            .sort((a, b) => b.count - a.count);
    }, [result]);

    const resetAndClose = () => {
        setPhase('confirm');
        setAcked(false);
        setListOpen(false);
        setSampleIdx(0);
        setResult(null);
        onClose();
    };

    const run = async () => {
        if (!canApprove) return;
        setPhase('working');
        try {
            const res = await onConfirm();
            if (res == null) {
                setResult(null);
                setPhase('error');
                return;
            }
            setResult(res);
            setPhase('done');
        } catch {
            setResult(null);
            setPhase('error');
        }
    };

    const tabLabel =
        tab === 'pending'
            ? 'Pending Promotion'
            : tab === 'link'
              ? 'Link Review'
              : 'Barcode Conflicts';
    const headline = filtered
        ? isLink
            ? `Accept AI for ${formatCpmCount(total)} links`
            : `Approve ${formatCpmCount(total)} pending records`
        : isLink
          ? `Accept AI for ${formatCpmCount(total)} selected links`
          : `Approve ${formatCpmCount(total)} selected records`;
    const subhead = filtered
        ? `Everything currently matching your filter. Check the sample below before you ${isLink ? 'accept these links' : 'promote them'} — a filter can cover more than it looks like.`
        : 'The rows you ticked on the queue list. You already saw each one, so this is just the final confirmation.';

    const impactSentence =
        (isLink
            ? `This will link ${formatCpmCount(counts[0])} Brands, ${formatCpmCount(counts[1])} Products, and ${formatCpmCount(counts[2])} Variants to their suggested master records`
            : `This will move ${formatCpmCount(counts[0])} Brands, ${formatCpmCount(counts[1])} Products, and ${formatCpmCount(counts[2])} Variants from Pending to Active`) +
        ` — ${formatCpmCount(total)} records in total` +
        (filtered
            ? `, spanning ${formatCpmCount(storeBrandCount)} distinct store brand${storeBrandCount === 1 ? '' : 's'}. This runs as one batch and cannot be undone record-by-record from here.`
            : '.');

    const impactTiles = useMemo(() => {
        if (tab === 'barcode') {
            return [{ label: 'Total', value: total, accent: true }];
        }
        const tiles: Array<{ label: string; value: number; accent: boolean }> = [
            { label: 'Brands', value: counts[0], accent: false },
            { label: 'Products', value: counts[1], accent: false },
            { label: 'Variants', value: counts[2], accent: false },
        ];
        if (filtered) {
            tiles.push({ label: 'Store brands', value: storeBrandCount, accent: false });
        }
        tiles.push({ label: filtered ? 'Records' : 'Total', value: total, accent: true });
        return tiles;
    }, [tab, counts, filtered, storeBrandCount, total]);

    const consequenceNote = filtered
        ? isLink
            ? `Once confirmed, all ${formatCpmCount(total)} links are written in a single pass and every affected tenant record starts reporting under its master immediately. This cannot be undone from this dialog — a wrong link can only be re-pointed one record at a time later in the Master Editor.`
            : `Once confirmed, all ${formatCpmCount(total)} records go live across every affected store brand in a single pass. This cannot be undone from this dialog — a wrong record can only be corrected or merged one at a time later in the Master Editor.`
        : isLink
          ? 'Linked records start reporting under their master immediately. If a link turns out to be wrong later, it can be unlinked or re-pointed from the Master Editor.'
          : 'Approved records become part of the active catalog immediately. If one turns out to be wrong later, it can still be corrected or merged from the Master Editor.';

    const sampleNote = isLink
        ? 'Each row shows the tenant record and the master it would be linked to. Low-confidence rows are only included if your filter allows them.'
        : filters.signals.includes('DUP')
          ? 'This batch includes records flagged as possible duplicates — check the sample closely, since some may need a merge instead.'
          : 'These records carry no near-duplicate flag, so none require a side-by-side comparison.';

    return (
        <CpmModal open={open} onClose={resetAndClose} maxWidth={760} zIndex={60}>
            {phase === 'confirm' ? (
                <div className="flex flex-col">
                    {filtered ? (
                        <div
                            className="h-1"
                            style={{
                                background: 'linear-gradient(90deg,#D4183D,#F0577A)',
                            }}
                        />
                    ) : null}
                    <div
                        className="flex items-start border-b border-[var(--cpm-border-default)]"
                        style={{ gap: 14, padding: '22px 24px 18px' }}
                    >
                        <span
                            className="shrink-0 inline-flex items-center justify-center"
                            style={{
                                width: 42,
                                height: 42,
                                borderRadius: 11,
                                background: filtered
                                    ? 'var(--cpm-shortfall-bg)'
                                    : 'var(--cpm-purple-tint-13)',
                                border: filtered
                                    ? '1px solid var(--cpm-shortfall-border)'
                                    : '1px solid transparent',
                                color: filtered
                                    ? 'var(--cpm-shortfall-text)'
                                    : 'var(--cpm-brand-purple)',
                            }}
                        >
                            {filtered ? (
                                <svg
                                    width="21"
                                    height="21"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                >
                                    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                    <path d="M12 9v4M12 17h.01" strokeLinecap="round" />
                                </svg>
                            ) : (
                                <svg
                                    width="19"
                                    height="19"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                >
                                    <path
                                        d="M20 6 9 17l-5-5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            )}
                        </span>
                        <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 6 }}>
                            <div className="flex items-center flex-wrap" style={{ gap: 9 }}>
                                <span
                                    className="inline-flex items-center rounded-full uppercase"
                                    style={{
                                        padding: '3px 10px',
                                        fontSize: 10,
                                        fontWeight: 800,
                                        letterSpacing: '0.05em',
                                        background: filtered
                                            ? 'var(--cpm-warning-bg)'
                                            : 'var(--cpm-success-bg)',
                                        border: `1px solid ${filtered ? 'var(--cpm-warning-border)' : 'var(--cpm-success-border)'}`,
                                        color: filtered
                                            ? 'var(--cpm-warning-text)'
                                            : 'var(--cpm-success-text-light)',
                                    }}
                                >
                                    {filtered ? 'Filtered batch' : 'Hand-picked selection'}
                                </span>
                                <span
                                    className="text-[var(--cpm-text-secondary)]"
                                    style={{ fontSize: 11.5, fontWeight: 700 }}
                                >
                                    {tabLabel}
                                </span>
                            </div>
                            <h2 className="m-0 !text-[22px] !font-extrabold !tracking-[-0.02em] !leading-[1.15] !text-[var(--cpm-brand-navy)]">
                                {headline}
                            </h2>
                            <p className="m-0 !text-[13px] !font-semibold !leading-[1.5] !text-[var(--cpm-text-secondary)]">
                                {subhead}
                            </p>
                            {tab === 'link' ? (
                                <p className="m-0 !text-[13px] !font-bold !leading-[1.5] !text-[var(--cpm-text-secondary)]">
                                    {CPM_LICENCE_EXCISE_COPY.BULK_NOTE}
                                </p>
                            ) : null}
                        </div>
                        <button
                            type="button"
                            onClick={resetAndClose}
                            className="shrink-0 inline-flex items-center justify-center bg-white border-[1.5px] border-[var(--cpm-border-strong)] text-[var(--cpm-text-secondary)]"
                            style={{ width: 30, height: 30, borderRadius: 8 }}
                        >
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.4"
                            >
                                <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
                            </svg>
                        </button>
                    </div>

                    <div
                        className="flex flex-col overflow-y-auto"
                        style={{ maxHeight: '58vh', padding: '20px 24px 22px', gap: 22 }}
                    >
                        {filtered ? (
                            <div
                                className="flex items-center bg-[var(--cpm-shortfall-bg)] border-[1.5px] border-[var(--cpm-shortfall-border)]"
                                style={{ gap: 12, padding: '14px 16px', borderRadius: 12 }}
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="var(--cpm-shortfall-text)"
                                    strokeWidth="2.2"
                                    className="shrink-0"
                                >
                                    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                    <path d="M12 9v4M12 17h.01" strokeLinecap="round" />
                                </svg>
                                <div className="flex flex-col" style={{ gap: 2 }}>
                                    <span
                                        className="text-[var(--cpm-shortfall-text)]"
                                        style={{
                                            fontSize: 13,
                                            fontWeight: 800,
                                            letterSpacing: '-0.01em',
                                        }}
                                    >
                                        High-stakes bulk action
                                    </span>
                                    <span
                                        className="text-[var(--cpm-shortfall-text-med)]"
                                        style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.45 }}
                                    >
                                        You are about to change {formatCpmCount(total)} live,
                                        tenant-facing records in one pass. Confirm only after the
                                        spot-check below looks right.
                                    </span>
                                </div>
                            </div>
                        ) : null}

                        {filtered ? (
                            <div className="flex flex-col" style={{ gap: 9 }}>
                                <span
                                    className="uppercase text-[var(--cpm-text-tertiary)]"
                                    style={{
                                        fontSize: 10.5,
                                        fontWeight: 800,
                                        letterSpacing: '0.08em',
                                    }}
                                >
                                    What is being matched
                                </span>
                                <div className="flex flex-wrap" style={{ gap: 7 }}>
                                    {criteria.map((c) => (
                                        <span
                                            key={c.k}
                                            className="inline-flex items-center rounded-full bg-[var(--cpm-surface-subtle)] border border-[var(--cpm-border-default)] text-[var(--cpm-text-primary)]"
                                            style={{
                                                gap: 7,
                                                padding: '6px 12px',
                                                fontSize: 12,
                                                fontWeight: 700,
                                            }}
                                        >
                                            <span
                                                className="uppercase text-[var(--cpm-text-tertiary)]"
                                                style={{
                                                    fontSize: 10,
                                                    fontWeight: 800,
                                                    letterSpacing: '0.05em',
                                                }}
                                            >
                                                {c.k}
                                            </span>
                                            <span>{c.v}</span>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ) : null}

                        {!filtered ? (
                            <div className="flex flex-col" style={{ gap: 10 }}>
                                <span
                                    className="uppercase text-[var(--cpm-text-tertiary)]"
                                    style={{
                                        fontSize: 10.5,
                                        fontWeight: 800,
                                        letterSpacing: '0.08em',
                                    }}
                                >
                                    Your selection
                                </span>
                                <div
                                    className="flex items-center bg-[var(--cpm-surface-subtle)] border border-[var(--cpm-border-default)]"
                                    style={{ gap: 12, padding: '13px 15px', borderRadius: 10 }}
                                >
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="var(--cpm-brand-purple)"
                                        strokeWidth="2.2"
                                        className="shrink-0"
                                    >
                                        <path d="M9 11l3 3L22 4" strokeLinecap="round" />
                                        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                                    </svg>
                                    <span
                                        className="flex-1 text-[var(--cpm-text-primary)]"
                                        style={{ fontSize: 13, fontWeight: 700 }}
                                    >
                                        {formatCpmCount(total)} records you hand-picked on the queue
                                        list. Nothing else is included.
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setListOpen((v) => !v)}
                                        className="shrink-0 text-[var(--cpm-brand-purple)] border-b border-[var(--cpm-brand-purple)]"
                                        style={{ fontSize: 12, fontWeight: 800 }}
                                    >
                                        {listOpen ? 'Hide the list' : 'View the list'}
                                    </button>
                                </div>
                                {listOpen ? (
                                    <div className="border border-[var(--cpm-border-default)] rounded-[10px] overflow-hidden">
                                        {rows.map((r, i) => (
                                            <div
                                                key={isPending(r) ? r._id : r._id}
                                                className="grid items-center bg-white border-b border-[var(--cpm-border-default)] last:border-b-0"
                                                style={{
                                                    gridTemplateColumns: '26px minmax(0,1fr) 82px',
                                                    gap: 10,
                                                    padding: '9px 13px',
                                                }}
                                            >
                                                <span
                                                    className="tabular text-[var(--cpm-text-tertiary)]"
                                                    style={{ fontSize: 11, fontWeight: 700 }}
                                                >
                                                    {i + 1}
                                                </span>
                                                <span
                                                    className="truncate text-[var(--cpm-text-primary)]"
                                                    style={{ fontSize: 12.5, fontWeight: 700 }}
                                                >
                                                    {rowName(r)}
                                                </span>
                                                <CpmTypeChip recordType={rowType(r)} />
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}

                        {filtered ? (
                            <div className="flex flex-col" style={{ gap: 11 }}>
                                <div
                                    className="flex items-end justify-between flex-wrap"
                                    style={{ gap: 14 }}
                                >
                                    <div className="flex flex-col" style={{ gap: 3 }}>
                                        <span
                                            className="uppercase text-[var(--cpm-text-tertiary)]"
                                            style={{
                                                fontSize: 10.5,
                                                fontWeight: 800,
                                                letterSpacing: '0.08em',
                                            }}
                                        >
                                            Spot-check
                                        </span>
                                        <span
                                            className="text-[var(--cpm-text-primary)]"
                                            style={{ fontSize: 13, fontWeight: 700 }}
                                        >
                                            6 records drawn at random from the{' '}
                                            {formatCpmCount(total)}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSampleIdx((i) => i + 1);
                                            setAcked(false);
                                        }}
                                        className="inline-flex items-center bg-white border-[1.5px] border-[var(--cpm-border-strong)] text-[var(--cpm-text-secondary)] hover:border-[var(--cpm-brand-purple)] hover:text-[var(--cpm-brand-navy)]"
                                        style={{
                                            gap: 7,
                                            height: 32,
                                            paddingInline: 13,
                                            borderRadius: 10,
                                            fontSize: 12,
                                            fontWeight: 800,
                                        }}
                                    >
                                        <svg
                                            width="13"
                                            height="13"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.2"
                                        >
                                            <path d="M3 12a9 9 0 0 1 9-9 9 9 0 0 1 6.7 3L21 8" />
                                            <path d="M21 3v5h-5" />
                                            <path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1-6.7-3L3 16" />
                                            <path d="M3 21v-5h5" />
                                        </svg>
                                        Show a different sample of 6
                                    </button>
                                </div>
                                <div className="border border-[var(--cpm-border-default)] rounded-[10px] overflow-hidden">
                                    {sample.map((s, i) => {
                                        const pack = isPending(s) ? packLabel(s) : packLabel(s);
                                        const brand = isPending(s)
                                            ? pendingBrandLabel(s)
                                            : s.storeBrandName || null;
                                        const createdVia = isPending(s)
                                            ? formatCreatedVia(s.provenance?.createdBy)
                                            : s.method;
                                        const createdOn = formatFlaggedDate(s.createdAt);
                                        return (
                                            <div
                                                key={s._id + String(i)}
                                                className="grid items-center"
                                                style={{
                                                    gridTemplateColumns:
                                                        'minmax(0,1.5fr) 84px minmax(0,1fr)',
                                                    gap: 14,
                                                    padding: '12px 14px',
                                                    background:
                                                        i % 2
                                                            ? 'var(--cpm-surface-subtle)'
                                                            : '#fff',
                                                    borderBottom:
                                                        i < sample.length - 1
                                                            ? '1px solid var(--cpm-border-default)'
                                                            : undefined,
                                                }}
                                            >
                                                <div
                                                    className="min-w-0 flex flex-col"
                                                    style={{ gap: 5 }}
                                                >
                                                    <span
                                                        className="truncate text-[var(--cpm-text-primary)]"
                                                        style={{
                                                            fontSize: 13.5,
                                                            fontWeight: 800,
                                                            letterSpacing: '-0.01em',
                                                        }}
                                                    >
                                                        {rowName(s)}
                                                    </span>
                                                    {brand || pack ? (
                                                        <span
                                                            className="flex items-center min-w-0"
                                                            style={{ gap: 8 }}
                                                        >
                                                            {brand ? (
                                                                <span
                                                                    className="truncate text-[var(--cpm-text-secondary)]"
                                                                    style={{
                                                                        fontSize: 12,
                                                                        fontWeight: 600,
                                                                    }}
                                                                >
                                                                    {brand}
                                                                </span>
                                                            ) : null}
                                                            {pack ? (
                                                                <span
                                                                    className="tabular text-[var(--cpm-text-tertiary)] whitespace-nowrap"
                                                                    style={{
                                                                        fontSize: 11.5,
                                                                        fontWeight: 700,
                                                                    }}
                                                                >
                                                                    {pack}
                                                                </span>
                                                            ) : null}
                                                        </span>
                                                    ) : null}
                                                </div>
                                                <CpmTypeChip recordType={rowType(s)} />
                                                <div
                                                    className="min-w-0 flex flex-col"
                                                    style={{ gap: 4 }}
                                                >
                                                    <span
                                                        className="truncate text-[var(--cpm-text-primary)]"
                                                        style={{ fontSize: 12.5, fontWeight: 700 }}
                                                    >
                                                        {createdVia}
                                                    </span>
                                                    <span
                                                        className="text-[var(--cpm-text-secondary)]"
                                                        style={{ fontSize: 11.5, fontWeight: 600 }}
                                                    >
                                                        {createdOn}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <span
                                    className="text-[var(--cpm-text-secondary)]"
                                    style={{ fontSize: 11.5, fontWeight: 600 }}
                                >
                                    {sampleNote}
                                </span>
                            </div>
                        ) : null}

                        <div className="flex flex-col" style={{ gap: 10 }}>
                            <span
                                className="uppercase text-[var(--cpm-text-tertiary)]"
                                style={{
                                    fontSize: 10.5,
                                    fontWeight: 800,
                                    letterSpacing: '0.08em',
                                }}
                            >
                                Impact
                            </span>
                            <div
                                className="grid"
                                style={{
                                    gridTemplateColumns:
                                        impactTiles.length === 1
                                            ? '200px'
                                            : `repeat(${impactTiles.length}, 1fr)`,
                                    gap: 10,
                                }}
                            >
                                {impactTiles.map((tile) => (
                                    <div
                                        key={tile.label}
                                        className="flex flex-col"
                                        style={{
                                            gap: 5,
                                            padding: '12px 13px',
                                            borderRadius: 10,
                                            border: tile.accent
                                                ? '1px solid transparent'
                                                : '1px solid var(--cpm-border-default)',
                                            background: tile.accent
                                                ? filtered
                                                    ? 'linear-gradient(150deg,#D4183D,#8E0B27)'
                                                    : 'var(--cpm-gradient-hero)'
                                                : 'var(--cpm-surface-subtle)',
                                        }}
                                    >
                                        <span
                                            className="uppercase"
                                            style={{
                                                fontSize: 10,
                                                fontWeight: 800,
                                                letterSpacing: '0.08em',
                                                color: tile.accent
                                                    ? 'rgba(255,255,255,.72)'
                                                    : 'var(--cpm-text-tertiary)',
                                            }}
                                        >
                                            {tile.label}
                                        </span>
                                        <span
                                            className="tabular"
                                            style={{
                                                fontSize: 22,
                                                fontWeight: 800,
                                                letterSpacing: '-0.02em',
                                                color: tile.accent
                                                    ? '#fff'
                                                    : 'var(--cpm-brand-navy)',
                                            }}
                                        >
                                            {formatCpmCount(tile.value)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <span
                                className="text-[var(--cpm-text-secondary)]"
                                style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.5 }}
                            >
                                {impactSentence}
                            </span>
                        </div>

                        <div
                            className="flex"
                            style={{
                                gap: 11,
                                padding: '13px 15px',
                                borderRadius: 10,
                                background: filtered
                                    ? 'var(--cpm-shortfall-bg)'
                                    : 'var(--cpm-info-bg)',
                                border: `1px solid ${filtered ? 'var(--cpm-shortfall-border)' : 'var(--cpm-info-border)'}`,
                            }}
                        >
                            <svg
                                width="15"
                                height="15"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke={
                                    filtered ? 'var(--cpm-shortfall-text)' : 'var(--cpm-info-icon)'
                                }
                                strokeWidth="2"
                                className="shrink-0 mt-px"
                            >
                                <circle cx="12" cy="12" r="10" />
                                <path d="M12 16v-4M12 8h.01" strokeLinecap="round" />
                            </svg>
                            <span
                                style={{
                                    fontSize: 12.5,
                                    fontWeight: filtered ? 700 : 600,
                                    lineHeight: 1.5,
                                    color: filtered
                                        ? 'var(--cpm-shortfall-text)'
                                        : 'var(--cpm-info-text)',
                                }}
                            >
                                {consequenceNote}
                            </span>
                        </div>
                    </div>

                    <div
                        className="flex items-center flex-wrap border-t-[1.5px] border-[var(--cpm-border-strong)]"
                        style={{
                            gap: 16,
                            padding: '16px 24px',
                            background: 'rgba(249,249,252,0.88)',
                        }}
                    >
                        {filtered ? (
                            <button
                                type="button"
                                onClick={() => setAcked((v) => !v)}
                                className="inline-flex items-center"
                                style={{
                                    gap: 10,
                                    padding: '8px 13px 8px 11px',
                                    borderRadius: 10,
                                    border: `1.5px solid ${acked ? 'var(--cpm-brand-purple)' : 'var(--cpm-border-strong)'}`,
                                    background: acked ? 'rgba(110,100,240,.07)' : '#fff',
                                }}
                            >
                                <span
                                    className="inline-flex items-center justify-center"
                                    style={{
                                        width: 18,
                                        height: 18,
                                        borderRadius: 5,
                                        border: `1.5px solid ${acked ? 'var(--cpm-brand-purple)' : 'var(--cpm-border-strong)'}`,
                                        background: acked ? 'var(--cpm-brand-purple)' : '#fff',
                                    }}
                                >
                                    {acked ? (
                                        <svg
                                            width="11"
                                            height="11"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="#fff"
                                            strokeWidth="3.2"
                                        >
                                            <path
                                                d="M20 6 9 17l-5-5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    ) : null}
                                </span>
                                <span
                                    style={{
                                        fontSize: 12.5,
                                        fontWeight: 800,
                                        color: acked
                                            ? 'var(--cpm-brand-navy)'
                                            : 'var(--cpm-text-secondary)',
                                    }}
                                >
                                    I&apos;ve reviewed this sample
                                </span>
                            </button>
                        ) : null}
                        <div className="ml-auto flex items-center" style={{ gap: 10 }}>
                            <CpmButton variant="outline" size="modal" onClick={resetAndClose}>
                                Cancel
                            </CpmButton>
                            <CpmButton
                                variant={filtered ? 'danger' : 'navy'}
                                size="modal"
                                disabled={!canApprove}
                                onClick={run}
                                leadingIcon={
                                    <svg
                                        width="15"
                                        height="15"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.4"
                                    >
                                        <path
                                            d="M20 6 9 17l-5-5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                }
                            >
                                {filtered
                                    ? `Approve all ${formatCpmCount(total)}`
                                    : `Approve these ${formatCpmCount(total)}`}
                            </CpmButton>
                        </div>
                        {filtered && !acked ? (
                            <span
                                className="w-full text-right text-[var(--cpm-warning-text)]"
                                style={{ fontSize: 11.5, fontWeight: 700 }}
                            >
                                Review the sample above before approving
                            </span>
                        ) : null}
                    </div>
                </div>
            ) : null}

            {phase === 'working' ? (
                <div
                    className="flex flex-col items-center"
                    style={{ padding: '52px 24px', gap: 16 }}
                >
                    <span
                        className="inline-flex items-center justify-center bg-[var(--cpm-purple-tint-13)] text-[var(--cpm-brand-purple)]"
                        style={{ width: 38, height: 38, borderRadius: 10 }}
                    >
                        <svg
                            width="19"
                            height="19"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            className="animate-spin"
                        >
                            <path d="M12 2v4M12 18v4M4.9 4.9l2.9 2.9M16.2 16.2l2.9 2.9M2 12h4M18 12h4M4.9 19.1l2.9-2.9M16.2 7.8l2.9-2.9" />
                        </svg>
                    </span>
                    <span
                        className="text-[var(--cpm-brand-navy)]"
                        style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.01em' }}
                    >
                        {isLink ? 'Linking' : 'Promoting'} {formatCpmCount(total)} records…
                    </span>
                    <span
                        className="text-[var(--cpm-text-secondary)]"
                        style={{ fontSize: 12.5, fontWeight: 600 }}
                    >
                        This can take a few seconds. Don&apos;t close this window.
                    </span>
                </div>
            ) : null}

            {phase === 'error' ? (
                <>
                    <div
                        className="flex flex-col items-center text-center"
                        style={{ padding: '44px 32px 26px', gap: 14 }}
                    >
                        <span
                            className="inline-flex items-center justify-center bg-[var(--cpm-shortfall-bg)] border border-[var(--cpm-shortfall-border)] text-[var(--cpm-shortfall-text)]"
                            style={{ width: 56, height: 56, borderRadius: 14 }}
                        >
                            <svg
                                width="26"
                                height="26"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                <path d="M12 9v4" />
                                <path d="M12 17h.01" />
                            </svg>
                        </span>
                        <span
                            className="text-[var(--cpm-brand-navy)]"
                            style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em' }}
                        >
                            The batch could not be sent
                        </span>
                        <span
                            className="text-[var(--cpm-text-secondary)] max-w-[440px]"
                            style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.5 }}
                        >
                            The catalog service did not accept the batch of {formatCpmCount(total)}{' '}
                            records. Nothing was approved and every record stays in the queue.
                        </span>
                    </div>
                    <div
                        className="flex items-center border-t-[1.5px] border-[var(--cpm-border-strong)]"
                        style={{
                            gap: 10,
                            padding: '16px 24px',
                            background: 'rgba(249,249,252,0.88)',
                        }}
                    >
                        <div className="ml-auto flex items-center" style={{ gap: 10 }}>
                            <CpmButton variant="outline" size="modal" onClick={resetAndClose}>
                                Close
                            </CpmButton>
                            <CpmButton
                                variant="navy"
                                size="modal"
                                onClick={run}
                                leadingIcon={
                                    <svg
                                        width="15"
                                        height="15"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.4"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                                        <path d="M3 3v5h5" />
                                    </svg>
                                }
                            >
                                Try again
                            </CpmButton>
                        </div>
                    </div>
                </>
            ) : null}

            {phase === 'done' && failed === 0 ? (
                <div
                    className="flex flex-col items-center text-center"
                    style={{ padding: '44px 32px 32px', gap: 14 }}
                >
                    <span
                        className="inline-flex items-center justify-center bg-[var(--cpm-success-bg)] border border-[var(--cpm-success-border)] text-[var(--cpm-success-text-light)]"
                        style={{ width: 56, height: 56, borderRadius: 14 }}
                    >
                        <svg
                            width="26"
                            height="26"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                        >
                            <path
                                d="M20 6 9 17l-5-5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </span>
                    <span
                        className="text-[var(--cpm-brand-navy)]"
                        style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em' }}
                    >
                        {formatCpmCount(total)}{' '}
                        {isLink ? 'records are now linked' : 'records are now Active'}
                    </span>
                    <span
                        className="text-[var(--cpm-text-secondary)]"
                        style={{
                            fontSize: 13,
                            fontWeight: 600,
                            lineHeight: 1.5,
                            maxWidth: 440,
                        }}
                    >
                        {isLink
                            ? `Each tenant record now points at its master.${remainingAfter != null ? ` The Link Review tab now holds ${formatCpmCount(Math.max(0, remainingAfter))} links.` : ''}`
                            : `They are part of the active catalog and visible to every store brand.${remainingAfter != null ? ` The Pending Promotion tab now holds ${formatCpmCount(Math.max(0, remainingAfter))} records.` : ''}`}
                    </span>
                    <div className="flex items-center" style={{ gap: 10, marginTop: 8 }}>
                        <CpmButton variant="navy" size="modal" onClick={resetAndClose}>
                            Back to the queue
                        </CpmButton>
                    </div>
                </div>
            ) : null}

            {phase === 'done' && failed > 0 ? (
                <div className="flex flex-col">
                    <div
                        className="flex items-start border-b border-[var(--cpm-border-default)]"
                        style={{ gap: 14, padding: '22px 24px 18px' }}
                    >
                        <span
                            className="shrink-0 inline-flex items-center justify-center bg-[var(--cpm-warning-bg)] border border-[var(--cpm-warning-border)] text-[var(--cpm-warning-text)]"
                            style={{ width: 42, height: 42, borderRadius: 11 }}
                        >
                            <svg
                                width="21"
                                height="21"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                            >
                                <path d="M9 11l3 3L22 4" />
                                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                            </svg>
                        </span>
                        <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 5 }}>
                            <h2 className="m-0 !text-[21px] !font-extrabold !tracking-[-0.02em] !leading-[1.15] !text-[var(--cpm-brand-navy)]">
                                {formatCpmCount(succeeded)} approved, {formatCpmCount(failed)}{' '}
                                couldn&apos;t be
                            </h2>
                            <p className="m-0 !text-[13px] !font-semibold !leading-[1.5] !text-[var(--cpm-text-secondary)]">
                                The batch finished with a partial result. Approved records are live
                                in the catalog now; the rest were held back because each is blocked
                                on a parent that isn&apos;t approved yet.
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-col" style={{ padding: '20px 24px 22px', gap: 18 }}>
                        <div className="grid grid-cols-2" style={{ gap: 12 }}>
                            <div
                                className="flex flex-col bg-[var(--cpm-success-bg)] border border-[var(--cpm-success-border)]"
                                style={{ gap: 6, padding: '15px 16px', borderRadius: 12 }}
                            >
                                <span
                                    className="inline-flex items-center uppercase text-[var(--cpm-success-text-light)]"
                                    style={{
                                        gap: 6,
                                        fontSize: 10.5,
                                        fontWeight: 800,
                                        letterSpacing: '0.07em',
                                    }}
                                >
                                    Approved
                                </span>
                                <span
                                    className="tabular text-[var(--cpm-success-text-light)]"
                                    style={{
                                        fontSize: 30,
                                        fontWeight: 800,
                                        letterSpacing: '-0.02em',
                                        lineHeight: 1,
                                    }}
                                >
                                    {formatCpmCount(succeeded)}
                                </span>
                                <span
                                    className="text-[var(--cpm-success-text-light)]"
                                    style={{ fontSize: 11.5, fontWeight: 600 }}
                                >
                                    Now live in the active catalog
                                </span>
                            </div>
                            <div
                                className="flex flex-col bg-[var(--cpm-shortfall-bg)] border border-[var(--cpm-shortfall-border)]"
                                style={{ gap: 6, padding: '15px 16px', borderRadius: 12 }}
                            >
                                <span
                                    className="inline-flex items-center uppercase text-[var(--cpm-shortfall-text)]"
                                    style={{
                                        gap: 6,
                                        fontSize: 10.5,
                                        fontWeight: 800,
                                        letterSpacing: '0.07em',
                                    }}
                                >
                                    Could not approve
                                </span>
                                <span
                                    className="tabular text-[var(--cpm-shortfall-text)]"
                                    style={{
                                        fontSize: 30,
                                        fontWeight: 800,
                                        letterSpacing: '-0.02em',
                                        lineHeight: 1,
                                    }}
                                >
                                    {formatCpmCount(failed)}
                                </span>
                                <span
                                    className="text-[var(--cpm-shortfall-text-med)]"
                                    style={{ fontSize: 11.5, fontWeight: 600 }}
                                >
                                    Left in Pending — nothing changed for these
                                </span>
                            </div>
                        </div>
                        <div className="flex flex-col" style={{ gap: 9 }}>
                            <span
                                className="uppercase text-[var(--cpm-text-tertiary)]"
                                style={{
                                    fontSize: 10.5,
                                    fontWeight: 800,
                                    letterSpacing: '0.08em',
                                }}
                            >
                                Why they failed
                            </span>
                            {failGroups.map((g) => (
                                <div
                                    key={g.reason}
                                    className="flex items-center bg-[var(--cpm-surface-subtle)] border border-[var(--cpm-border-default)]"
                                    style={{ gap: 13, padding: '13px 15px', borderRadius: 10 }}
                                >
                                    <span
                                        className="shrink-0 inline-flex items-center justify-center rounded-full bg-[var(--cpm-shortfall-bg)] border border-[var(--cpm-shortfall-border)] text-[var(--cpm-shortfall-text)] tabular"
                                        style={{
                                            minWidth: 34,
                                            height: 26,
                                            paddingInline: 9,
                                            fontSize: 13,
                                            fontWeight: 800,
                                        }}
                                    >
                                        {formatCpmCount(g.count)}
                                    </span>
                                    <div
                                        className="flex-1 min-w-0 flex flex-col"
                                        style={{ gap: 2 }}
                                    >
                                        <span
                                            className="text-[var(--cpm-brand-navy)]"
                                            style={{
                                                fontSize: 13,
                                                fontWeight: 800,
                                                letterSpacing: '-0.01em',
                                            }}
                                        >
                                            {failGroupLabel(g.reason)}
                                        </span>
                                        <span
                                            className="text-[var(--cpm-text-secondary)]"
                                            style={{
                                                fontSize: 11.5,
                                                fontWeight: 600,
                                                lineHeight: 1.4,
                                            }}
                                        >
                                            {failGroupHint(g.reason)}
                                        </span>
                                    </div>
                                    {onJumpBlocked ? (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                onJumpBlocked();
                                                resetAndClose();
                                            }}
                                            className="shrink-0 inline-flex items-center bg-white border-[1.5px] border-[var(--cpm-border-strong)] text-[var(--cpm-text-secondary)]"
                                            style={{
                                                gap: 6,
                                                height: 32,
                                                paddingInline: 13,
                                                borderRadius: 9,
                                                fontSize: 12,
                                                fontWeight: 800,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Show these in the list
                                            <svg
                                                width="13"
                                                height="13"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.2"
                                            >
                                                <path d="M5 12h14" />
                                                <path d="m12 5 7 7-7 7" />
                                            </svg>
                                        </button>
                                    ) : null}
                                </div>
                            ))}
                            <span
                                className="text-[var(--cpm-text-secondary)]"
                                style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.5 }}
                            >
                                Nothing changed for the {formatCpmCount(failed)} that failed — they
                                stay in Pending exactly as they were.
                            </span>
                        </div>
                    </div>
                    <div
                        className="flex items-center border-t-[1.5px] border-[var(--cpm-border-strong)]"
                        style={{
                            gap: 10,
                            padding: '16px 24px',
                            background: 'rgba(249,249,252,0.88)',
                        }}
                    >
                        <CpmButton
                            variant="navy"
                            size="modal"
                            className="ml-auto"
                            onClick={resetAndClose}
                        >
                            Back to the queue
                        </CpmButton>
                    </div>
                </div>
            ) : null}
        </CpmModal>
    );
}
